import type { Permissao } from '@triade/contracts';
import { rotuloPermissao } from '@/components/perfis/tipos';
import { ApiError } from './api';
import { textoIssue } from './issues';

/** Como uma tela explica um erro da API: titulo curto, mensagem e uma linha de detalhe tecnico (para o suporte). */
export interface DescricaoErro {
  titulo: string;
  mensagem: string;
  /** Sempre preenchido: `HTTP <status> · código <code>` (o que existir), ou a falha de rede. */
  detalhe: string;
  /** Linhas extras (422/400 com `issues` da validacao). */
  itens: string[];
}

/**
 * Traduz qualquer erro de requisicao em texto para a tela. So apresentacao: a mensagem vem da API.
 * - 401 sessao expirada; 403 "Sem permissao para ..." (permissoesFaltantes traduzidas; sem lista, `area` ou "esta tela");
 * - 404 nao encontrado; 400/409/422 a mensagem da API (e os `issues`); 5xx erro no servidor com o id da requisicao;
 * - falha de rede (nao e ApiError): sem conexao com a API.
 * `area` e o nome da tela no plural/locucao (ex.: "os cargos"), usado so no 403 sem lista de permissoes.
 */
export function descreverErro(erro: unknown, area?: string): DescricaoErro {
  if (!(erro instanceof ApiError)) {
    return {
      titulo: 'Sem conexão com a API',
      mensagem: 'Verifique sua conexão e tente de novo.',
      detalhe: 'sem resposta da API',
      itens: [],
    };
  }

  const { status, code, body } = erro;
  const detalhe = `HTTP ${status}${code ? ` · código ${code}` : ''}`;
  const apiMensagem = erro.message;

  if (status === 401 && code === 'SESSAO_INVALIDA') {
    return { titulo: 'Sessão expirada', mensagem: 'Entre de novo para continuar.', detalhe, itens: [] };
  }

  if (status === 403) {
    const faltantes = body?.permissoesFaltantes ?? [];
    const titulo =
      faltantes.length > 0
        ? `Sem permissão para: ${faltantes.map((p) => rotuloPermissao(p as Permissao)).join(', ')}`
        : `Sem permissão para ${area ?? 'esta tela'}`;
    return { titulo, mensagem: apiMensagem, detalhe, itens: [] };
  }

  if (status === 404) {
    return { titulo: 'Não encontrado', mensagem: apiMensagem, detalhe, itens: [] };
  }

  if (status >= 500) {
    const mensagem = body?.requestId ? `${apiMensagem} · id da requisição: ${body.requestId}` : apiMensagem;
    return { titulo: 'Erro no servidor', mensagem, detalhe, itens: [] };
  }

  const itens = (body?.issues ?? []).map(textoIssue);
  // Com issues, a mensagem do ApiError ja e o resumo "Corrija: ..." e os itens repetem cada ponto: a tela mostra os itens.
  if (itens.length > 0) return { titulo: 'Corrija os campos', mensagem: body?.message ?? apiMensagem, detalhe, itens };
  return { titulo: 'Não foi possível concluir', mensagem: apiMensagem, detalhe, itens };
}

/** Erros em que tentar de novo nao adianta (permissao, nao encontrado): a tela nao mostra o botao. */
export function ehErroTentavel(erro: unknown): boolean {
  return !(erro instanceof ApiError && (erro.status === 403 || erro.status === 404));
}
